import { type User, userSchema } from '@si/types';
import { get } from '@si/utils';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from 'src/useAuth';

import { useSalesDemoMode, useSalesReadOnly } from '../features/sales-demo/salesDemoMode';
import { queryKeys } from './queryKeys';

interface Params {
  userId?: string;
}

const emptyObject = {} as const;

export function useUser({ userId }: Params = emptyObject) {
  const { authenticated, token } = useAuth();

  const url = `/users/${userId || 'current'}`;
  const queryKey = queryKeys.getUser(userId || 'current');

  const {
    data: user,
    error,
    isLoading,
  } = useQuery<User>({
    queryKey,
    queryFn: () => get({ url, schema: userSchema }),
    enabled: authenticated || !!token,
  });

  const demoMode = useSalesDemoMode();
  const preview = useSalesReadOnly();
  const readOnly = demoMode || preview;
  return {
    user:
      user && readOnly
        ? { ...user, isSiAdmin: false, isSiOverlord: false, isDistributorUser: false }
        : user,
    canUseSalesDemo: user?.isSiAdmin === true,
    canGenerateSalesReport: readOnly && user?.isSiAdmin === true,
    canEditSalesRoofSpecifications: readOnly && user?.isSiAdmin === true,
    error,
    isLoading,
  };
}
